/* eslint-disable max-lines */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { nudgeSegmentTime } from '@utils/editors/nudge-segment-time.ts';
import {
  startsTextUndoStep,
  TextUndoStep
} from '@utils/editors/text-undo-step.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Descendant } from 'slate';
import { ReactEditor } from 'slate-react';
import Peaks, { PeaksInstance, Segment } from 'peaks.js';
import isEqual from 'fast-deep-equal';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  CaptionScriptixDescendant,
  DocumentSubtitleSegmentV1,
  DocumentSubtitleV1
} from '@utils/types/editors/subtitles/subtitle.ts';
import {
  DocumentObjectInnerDocument,
  DocumentResult
} from '@utils/types/editors/generic.ts';
import { useToggleFinishedStatus } from '@utils/api/magic-links/toggle-finalize-document.tsx';
import { useClearTextSharedDocument } from '@utils/api/magic-links/use-clear-text-shared-document.tsx';
import { useResetSharedDocument } from '@utils/api/magic-links/use-reset-shared-document.tsx';
import { useUpdateSharedDocument } from '@utils/api/magic-links/use-update-shared-document.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import {
  clearSharedCaptionFromLocalStorage,
  loadSharedCaptionFromLocalStorage,
  markAsSynced,
  saveSharedCaptionToLocalStorage
} from '@utils/localStorage/shared-caption-storage.ts';
import { resolveDraftRecovery } from '@utils/localStorage/recovery-decision.ts';
import {
  DraftConflictData,
  forceCloudSourceRequested
} from '@utils/localStorage/draft-conflict.ts';
import {
  hasPendingLocalChanges,
  resolveAutosave
} from '@utils/editors/autosave-decision.ts';
import {
  getBaseline,
  lastModifiedFromUpdateResponse,
  probeSharedServerLastModified,
  setBaseline,
  syncKey
} from '@utils/editors/document-sync-baseline.ts';
import type {
  SharedCaptionContextType,
  State
} from './shared-caption-context-state';
import { initialState, reducer } from './shared-caption-context-state';

export function useSharedCaptionContextLogic({
  session,
  document: captionDocument,
  token
}: {
  session?: Session;
  document?: DocumentResult;
  token?: string;
}): {
  contextValue: SharedCaptionContextType;
  conflict: DraftConflictData<DocumentSubtitleV1> | null;
  resolveConflictKeepLocal: () => void;
  resolveConflictLoadCloud: () => void;
} {
  const { t } = useTranslation();

  // Track if we recovered data from localStorage - if true, prevent API overwrites
  const hasRecoveredData = useRef(false);
  // Show the "edited elsewhere" notice at most once per editor session, so a
  // conflicted tab does not toast every 120 seconds.
  const hasWarnedRemoteEdit = useRef(false);

  // Check for localStorage recovery before initializing state (only once)
  const initialStateRef = useRef<State | null>(null);
  // Holds the unresolved local-vs-cloud conflict, if any (see DraftConflictModal).
  const initialConflictRef =
    useRef<DraftConflictData<DocumentSubtitleV1> | null>(null);

  if (initialStateRef.current === null) {
    const apiDocument = captionDocument?.result.document as DocumentSubtitleV1;
    let recoveredData: {
      document: DocumentSubtitleV1;
      undoStack: DocumentSubtitleV1[];
      redoStack: DocumentSubtitleV1[];
      isDirty: boolean;
      recoveryTimestamp: number;
    } | null = null;

    // The "view cloud version" tab bypasses local recovery entirely.
    const forceCloud = forceCloudSourceRequested();

    // Try to recover from localStorage if available
    if (session && captionDocument && !forceCloud) {
      const sessionId = session.session_id;
      const documentId = captionDocument.result.id;
      const localData = loadSharedCaptionFromLocalStorage(
        sessionId,
        documentId
      );

      if (localData && localData.document) {
        // Get server document timestamp
        let serverTimestamp = 0;
        if (captionDocument.result.last_modified) {
          const parsedTime = new Date(
            captionDocument.result.last_modified
          ).getTime();
          serverTimestamp = isNaN(parsedTime) ? 0 : parsedTime;
        }
        const localTimestamp = localData.lastLocalSave;
        const documentsDiffer = !isEqual(localData.document, apiDocument);

        // Reconcile the local draft with the server copy:
        //  - 'recover'  → genuine unsynced edits, server not ahead: load local.
        //  - 'server'   → local is stale/clean: server is authoritative.
        //  - 'conflict' → both advanced independently: ask the user (block the
        //    editor behind DraftConflictModal). Never silently drop either side
        //    (data-loss guard, Smartcomtv incident 2026-06).
        let decision = resolveDraftRecovery({
          localIsDirty: localData.isDirty,
          localLastSave: localTimestamp,
          localLastServerSync: localData.lastServerSync,
          serverLastModified: serverTimestamp,
          documentsDiffer
        });

        // Empty-draft guard (Den Haag incident, 2026-06): an init-time race or a
        // stale autosave can persist a localStorage draft with zero segments.
        // Recovering it blanks the editor ("No segments available") even though
        // the server copy is intact — and a follow-up autosave could then push
        // the empty doc back and wipe the server. An empty local draft is never a
        // legitimate edit when the server still has segments, so the server wins.
        const localSegmentCount = localData.document.document?.length ?? 0;
        const serverSegmentCount = apiDocument?.document?.length ?? 0;
        if (localSegmentCount === 0 && serverSegmentCount > 0) {
          decision = 'server';
        }

        if (decision === 'recover') {
          recoveredData = {
            document: localData.document,
            undoStack: localData.undoStack || [],
            redoStack: localData.redoStack || [],
            isDirty: false,
            recoveryTimestamp: localTimestamp || Date.now()
          };
        } else if (decision === 'conflict') {
          // Keep the server copy as the safe live baseline; capture the local
          // draft so the modal can offer it. Nothing is applied until the user
          // chooses, so no autosave can clobber either version.
          initialConflictRef.current = {
            localDocument: localData.document,
            localUndo: localData.undoStack || [],
            localRedo: localData.redoStack || [],
            localTimestamp: localTimestamp || 0,
            serverTimestamp,
            localCount: localData.document.document?.length,
            serverCount: apiDocument?.document?.length
          };
        }
      }
    }

    const finalDocument = recoveredData?.document ?? apiDocument;

    // Set flag to prevent API from overwriting recovered data
    hasRecoveredData.current = !!recoveredData;

    // Set correct save status based on recovery
    let saveStatus: 'unsaved' | 'saved-locally' | 'synced' = 'synced';
    if (recoveredData) {
      // If we recovered from localStorage, we have unsaved changes
      saveStatus = recoveredData.recoveryTimestamp
        ? 'saved-locally'
        : 'unsaved';
    }

    initialStateRef.current = {
      ...initialState,
      session,
      token,
      document: finalDocument,
      documentResult: captionDocument,
      undoStack: recoveredData?.undoStack ?? [],
      redoStack: recoveredData?.redoStack ?? [],
      isDirty: recoveredData?.isDirty ?? false,
      saveStatus,
      lastLocalSave: recoveredData?.recoveryTimestamp ?? null
    };
  }

  const [state, dispatch] = useReducer(reducer, initialStateRef.current);

  // Unresolved local-vs-cloud conflict. While set, the editor is blocked behind
  // DraftConflictModal so neither version can be silently overwritten.
  const [conflict, setConflict] =
    useState<DraftConflictData<DocumentSubtitleV1> | null>(
      () => initialConflictRef.current
    );

  const {
    setTime,
    currentTime,
    peaksInstance,
    initializePeaks: initializePeaksInAudio,
    ref: mediaRef
  } = useAudio();
  const currentEditorRef = useRef<ReactEditor | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { mutate: clearDocumentText, isLoading: cleanDocumentLoading } =
    useClearTextSharedDocument();
  const { mutate: updateDocument, isLoading: saveDocumentLoading } =
    useUpdateSharedDocument();
  const { mutate: resetDocument, isLoading: documentResetting } =
    useResetSharedDocument();
  const { mutate: toggleStatus, isLoading: finishedLoading } =
    useToggleFinishedStatus();

  // Use the recovered document from initial state, not the API document
  const documentRef = useRef<DocumentSubtitleV1 | undefined>(
    initialStateRef.current.document
  );

  // Refs for undo/redo stacks to avoid stale closures
  const undoStackRef = useRef<DocumentSubtitleV1[]>(
    initialStateRef.current.undoStack
  );
  const redoStackRef = useRef<DocumentSubtitleV1[]>(
    initialStateRef.current.redoStack
  );

  // localStorage debounce timer
  const localStorageSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const LOCALSTORAGE_DEBOUNCE_MS = 1000; // 1 second

  // Peaks drag tracking — prevent position resets during an active drag
  const isDraggingRef = useRef(false);
  const draggingSegmentIdRef = useRef<string | null>(null);

  // Helper functions for save status
  const setSaveStatus = useCallback(
    (status: 'unsaved' | 'saved-locally' | 'synced' | 'saving') => {
      dispatch({ type: 'SET_SAVE_STATUS', payload: status });
    },
    []
  );

  const setLastLocalSave = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_LOCAL_SAVE', payload: timestamp });
  }, []);

  const setLastServerSync = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_SERVER_SYNC', payload: timestamp });
  }, []);

  // Debounced localStorage save
  const debouncedLocalStorageSave = useCallback(() => {
    if (!state.session || !state.documentResult) return;

    // Clear existing timer
    if (localStorageSaveTimer.current) {
      clearTimeout(localStorageSaveTimer.current);
    }

    // Set new timer
    localStorageSaveTimer.current = setTimeout(() => {
      // Use refs instead of state to avoid stale closures
      const currentDocument = documentRef.current;
      if (!currentDocument) return;

      // Never persist an empty draft (Den Haag incident, 2026-06): a transient
      // empty document during load/init must not be autosaved, or it gets
      // recovered on the next open and blanks the editor. Captions are never
      // legitimately emptied to zero segments via autosave.
      if (!currentDocument.document || currentDocument.document.length === 0) {
        return;
      }

      const sessionId = state.session!.session_id;
      const documentId = state.documentResult!.result.id;

      // IMPORTANT: Always mark as dirty when saving from a document change
      // Only markAsSynced() should set isDirty to false
      const success = saveSharedCaptionToLocalStorage(sessionId, documentId, {
        document: currentDocument,
        undoStack: undoStackRef.current,
        redoStack: redoStackRef.current,
        isDirty: true, // Always true for debounced saves
        filename: state.documentResult!.result.filename,
        timecodeOffset: state.documentResult!.result.timecode_offset
      });

      if (success) {
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }, LOCALSTORAGE_DEBOUNCE_MS);
  }, [state.session, state.documentResult, setSaveStatus, setLastLocalSave]);

  // Keep refs in sync with state
  useEffect(() => {
    undoStackRef.current = state.undoStack;
    redoStackRef.current = state.redoStack;
  }, [state.undoStack, state.redoStack]);

  // Track if this is the initial mount to prevent overwriting recovered document
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the first run since we already set the initial document (possibly recovered)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // If we recovered data from localStorage, don't let API overwrite it until user saves
    if (hasRecoveredData.current) {
      return;
    }

    if (captionDocument) {
      dispatch({
        type: 'SET_DOCUMENT',
        payload: captionDocument.result.document as DocumentSubtitleV1
      });
      dispatch({ type: 'SET_DOCUMENT_RESULT', payload: captionDocument });
    }
  }, [captionDocument]);

  const setDocument = (document: DocumentSubtitleV1) => {
    if (document === state.document) return;
    addVersionChange(document);
    dispatch({ type: 'SET_DOCUMENT', payload: document });
    documentRef.current = document;
    if (peaksInstance && currentTime) {
      addVisibleSegments(peaksInstance, document, currentTime * 1000);
    }

    setIsDirty(true);
    setSaveStatus('unsaved');
    debouncedLocalStorageSave();
  };

  const setQuery = (query: string) => {
    dispatch({ type: 'SET_QUERY', payload: query });
  };

  const setEditor = (editor: ReactEditor | null) => {
    currentEditorRef.current = editor;
  };

  const reset = () => {
    if (!state.session || !state.documentResult) return;

    toast(t('resetting-document'));

    resetDocument(
      {
        sessionId: state.session.session_id,
        documentId: state.documentResult.result.id
      },
      {
        onSuccess: freshDocument => {
          toast.success(t('caption-has-been-reset-successfully'));

          // Clear localStorage to prevent recovery of old data
          clearSharedCaptionFromLocalStorage(
            state.session!.session_id,
            state.documentResult!.result.id
          );

          // Update local state with fresh document
          const newDocument = freshDocument.result
            .document as DocumentSubtitleV1;
          dispatch({ type: 'SET_DOCUMENT', payload: newDocument });
          dispatch({ type: 'SET_DOCUMENT_RESULT', payload: freshDocument });
          documentRef.current = newDocument;

          // Clear undo/redo stacks
          clearHistoryStack();
          undoStackRef.current = [];
          redoStackRef.current = [];

          // Force editor to re-render with new document
          dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });

          // Reset recovery flag so fresh data from server is used
          hasRecoveredData.current = false;

          // Reset dirty state
          dispatch({ type: 'SET_IS_DIRTY', payload: false });
          setSaveStatus('synced');
          setLastServerSync(Date.now());

          // Refresh Peaks view with new document if available
          if (peaksInstance && currentTime) {
            addVisibleSegments(peaksInstance, newDocument, currentTime * 1000);
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const fallback = t('failed-to-reset-caption');
          const data = error?.response?.data;
          const message =
            data?.error_description ||
            data?.detail ||
            data?.error ||
            data?.message ||
            fallback;

          toast.error(message);
        }
      }
    );
  };

  const cleanDocument = () => {
    if (!state.session || !state.documentResult) return;

    clearDocumentText(
      {
        sessionId: state.session?.session_id,
        documentId: state.documentResult?.result.id
      },
      {
        onSuccess: () => {
          toast.success('Caption changes have been cleared out');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error.response?.data?.detail ||
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Failed to clear caption';

          toast.error(errorMessage);
        }
      }
    );
  };

  const addListItem = (id: number) => {
    if (!state.document || !state.document.document) return;

    const currentItemIndex = state.document.document.findIndex(
      d => d.id === id
    );
    if (currentItemIndex === -1) return;

    const currentItem = state.document.document[currentItemIndex];
    const nextItem = state.document.document[currentItemIndex + 1];

    const MIN_DURATION = 100;
    const DEFAULT_DURATION = 1000;

    const newStartTime = currentItem.stop + 1;

    let newStopTime;

    if (nextItem) {
      const availableSpace = nextItem.start - newStartTime;

      if (availableSpace < MIN_DURATION) {
        const availableMs = Math.max(0, availableSpace);
        toast(
          t(
            'insufficient-space-between-segments',
            `Not enough space to add a new segment. Only ${availableMs}ms available between segments (minimum ${MIN_DURATION}ms required). Try adjusting the timing of adjacent segments first.`
          ),
          { id: 'insufficient-space-between-segments', icon: '⚠️' }
        );
        return;
      }

      const duration = Math.min(availableSpace - 1, DEFAULT_DURATION);
      newStopTime = newStartTime + duration;
    } else {
      const currentDuration = currentItem.stop - currentItem.start;
      const duration = Math.max(currentDuration, DEFAULT_DURATION);
      newStopTime = newStartTime + duration;
    }

    // Use a unique new ID (max existing + 1) to avoid renaming existing segments,
    // which would cause peaks.js to lose track of their positions.
    const maxId = Math.max(...state.document.document.map(item => item.id));
    const newItem: DocumentSubtitleSegmentV1 = {
      id: maxId + 1,
      start: newStartTime,
      stop: newStopTime,
      text: '',
      speaker: ''
    };

    const modifiedDocument = [...state.document.document];
    modifiedDocument.splice(currentItemIndex + 1, 0, newItem);

    setDocument({
      ...state.document,
      document: modifiedDocument
    });
    setIsDirty(true);
  };

  const removeListItem = (id: number) => {
    if (!state.document || !state.document.document) return;

    // Filter out the item to be removed
    const updatedDocument = state.document.document.filter(
      item => item.id !== id
    );

    setDocument({
      ...state.document,
      document: updatedDocument
    });
  };

  const splitListItem = (id: number) => {
    if (!state.document || !state.document.document) return;

    const currentItemIndex = state.document.document.findIndex(
      d => d.id === id
    );
    const lastItem = state.document.document[currentItemIndex];

    // Prevent splitting if text length is less than or equal to 2
    if (!lastItem.text || lastItem.text.trim().length <= 2) {
      toast.error(t('segment-is-too-short-to-split'));
      return;
    }

    const middleTimes = (lastItem.stop + lastItem.start) / 2;
    const middleTime = Math.floor(middleTimes);

    const middleIndex = Math.floor(lastItem.text.length / 2);
    const nearestSpace =
      lastItem.text.lastIndexOf(' ', middleIndex) + 1 || middleIndex;

    const maxId = Math.max(...state.document.document.map(item => item.id));
    const newItem: DocumentSubtitleSegmentV1 = {
      id: maxId + 1,
      start: middleTime,
      stop: lastItem.stop,
      text: lastItem.text.substring(nearestSpace).trim(),
      speaker: lastItem.speaker
    };

    const modifiedDocument = [...state.document.document];
    modifiedDocument[currentItemIndex] = {
      ...lastItem,
      stop: middleTime,
      text: lastItem.text.substring(0, nearestSpace).trim()
    };

    modifiedDocument.splice(currentItemIndex + 1, 0, newItem);

    setDocument({
      ...state.document,
      document: modifiedDocument
    });
  };

  const joinListItems = (id: number) => {
    if (!state.document || !state.document.document) return;

    const currentItemIndex = state.document.document.findIndex(
      d => d.id === id
    );

    // Check if current item exists and there is a next item
    if (currentItemIndex === -1) {
      toast.error(t('segment-not-found'));
      return;
    }

    const currentItem = state.document.document[currentItemIndex];
    const nextItem = state.document.document[currentItemIndex + 1];

    if (!nextItem) {
      toast.error(t('no-next-segment-to-join'));
      return;
    }

    // Merge text with newline separator
    const mergedText = currentItem.text + '\n' + nextItem.text;

    // Create merged segment using start of first and stop of second
    const mergedSegment: DocumentSubtitleSegmentV1 = {
      ...currentItem,
      stop: nextItem.stop,
      text: mergedText
    };

    // Remove the next item and update IDs of subsequent items
    const modifiedDocument = state.document.document
      .filter((_, index) => index !== currentItemIndex + 1)
      .map((item, index) => {
        if (index === currentItemIndex) {
          return mergedSegment;
        }
        // Decrement IDs of items after the removed segment
        if (item.id > nextItem.id) {
          return { ...item, id: item.id - 1 };
        }
        return item;
      });

    setDocument({
      ...state.document,
      document: modifiedDocument
    });
    toast.success(t('segments-joined-successfully'));
  };

  const setClickedIndex = (id: number | null) => {
    dispatch({ type: 'SET_CLICKED_INDEX', payload: id });
  };

  const clearHistoryStack = () => {
    dispatch({ type: 'SET_UNDO_STACK', payload: [] });
    dispatch({ type: 'SET_REDO_STACK', payload: [] });
  };

  // Reduced from 200 to 50 to prevent memory leaks (see docs/MEMORY_OPTIMIZATION.md)
  // Each undo entry stores a full document copy (~2MB for large docs)
  // 50 entries is still generous (most users need <20) and reduces memory by 75%
  const MAX_HISTORY = 50;

  // Current typing run; see startsTextUndoStep.
  const textUndoStepRef = useRef<TextUndoStep>(null);
  const [historyVersion, setHistoryVersion] = useState(0);

  const addVersionChange = (newDocument?: DocumentSubtitleV1) => {
    textUndoStepRef.current = null;
    if (
      !state.document ||
      JSON.stringify(state.document) === JSON.stringify(newDocument || {})
    )
      return;

    const trimmedUndo = [...state.undoStack, state.document].slice(
      -MAX_HISTORY
    );

    dispatch({
      type: 'SET_UNDO_STACK',
      payload: trimmedUndo
    });
    dispatch({ type: 'SET_REDO_STACK', payload: [] });

    // Save undo/redo stacks to localStorage (always mark as dirty)
    if (state.session && state.documentResult) {
      const sessionId = state.session.session_id;
      const documentId = state.documentResult.result.id;
      saveSharedCaptionToLocalStorage(sessionId, documentId, {
        undoStack: trimmedUndo,
        redoStack: [],
        isDirty: true
      });
    }
  };

  const undo = () => {
    textUndoStepRef.current = null;
    if (state.undoStack.length === 0) return;

    const undoStackCopy = [...state.undoStack];
    const lastState = undoStackCopy.pop();

    if (lastState) {
      const newRedoStack = state.document
        ? [...state.redoStack, state.document]
        : state.redoStack;

      if (state.document) {
        dispatch({
          type: 'SET_REDO_STACK',
          payload: newRedoStack
        });
      }
      dispatch({ type: 'SET_UNDO_STACK', payload: undoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: lastState });
      documentRef.current = lastState;
      setHistoryVersion(v => v + 1);

      // Save to localStorage (always mark as dirty since undo is a change)
      if (state.session && state.documentResult) {
        const sessionId = state.session.session_id;
        const documentId = state.documentResult.result.id;
        saveSharedCaptionToLocalStorage(sessionId, documentId, {
          document: lastState,
          undoStack: undoStackCopy,
          redoStack: newRedoStack,
          isDirty: true
        });
        // Keep the React flag in step with the persisted draft (issue #2347):
        // a dirty draft the autosave gate reads as clean never reaches the
        // server.
        setIsDirty(true);
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  };

  const redo = () => {
    textUndoStepRef.current = null;
    if (state.redoStack.length === 0) return;

    const redoStackCopy = [...state.redoStack];
    const nextState = redoStackCopy.pop();

    if (nextState) {
      const newUndoStack = state.document
        ? [...state.undoStack, state.document]
        : state.undoStack;

      if (state.document) {
        dispatch({
          type: 'SET_UNDO_STACK',
          payload: newUndoStack
        });
      }
      dispatch({ type: 'SET_REDO_STACK', payload: redoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: nextState });
      documentRef.current = nextState;
      setHistoryVersion(v => v + 1);

      // Save to localStorage (always mark as dirty since redo is a change)
      if (state.session && state.documentResult) {
        const sessionId = state.session.session_id;
        const documentId = state.documentResult.result.id;
        saveSharedCaptionToLocalStorage(sessionId, documentId, {
          document: nextState,
          undoStack: newUndoStack,
          redoStack: redoStackCopy,
          isDirty: true
        });
        setIsDirty(true);
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  };

  const applyStyle = (
    style: 'bold' | 'italic' | 'underlined' | 'color' | 'strike' | 'position',
    param2?: string
  ) => {
    if (!state.document) return;

    // clickedIndex is always a segment ID; fall back to current-time segment then first segment
    let targetId: number | null = state.clickedIndex;
    if (targetId === null || targetId === undefined) {
      const currentTimeMs = currentTime * 1000;
      const seg = state.document.document.find(
        s => currentTimeMs >= s.start && currentTimeMs <= s.stop
      );
      targetId = seg?.id ?? null;
    }
    if (targetId === null && state.document.document.length > 0) {
      targetId = state.document.document[0].id;
    }
    if (targetId === null) return;

    const updatedDocument = {
      ...state.document,
      document: state.document.document.map(segment => {
        if (segment.id !== targetId) return segment;

        const updatedSegment = { ...segment };
        switch (style) {
          case 'color':
            if (param2) {
              updatedSegment.color = param2;
            }
            break;
          case 'bold':
          case 'italic':
          case 'underlined':
          case 'strike':
            updatedSegment[style] = !updatedSegment[style];
            break;
          case 'position':
            if (param2 !== undefined) {
              // Position must be an integer 0-100, or undefined for default (bottom)
              const posNum = parseInt(param2, 10);
              if (!isNaN(posNum)) {
                const clampedPos = Math.max(0, Math.min(100, posNum));
                // Store undefined for default (100 = bottom) to minimize payload
                updatedSegment.position =
                  clampedPos === 100 ? undefined : String(clampedPos);
              }
            }
            break;
          default:
            break;
        }

        return updatedSegment;
      })
    };
    setIsDirty(true);
    setDocument(updatedDocument);
  };

  const applyFontToAll = (fontFamily: string) => {
    if (!state.document) return;
    const updatedDocument = {
      ...state.document,
      document: state.document.document.map(segment => ({
        ...segment,
        fontFamily: fontFamily || undefined
      }))
    };
    setDocument(updatedDocument);
    setIsDirty(true);
  };

  const changeSegmentTime = (
    id: number,
    field: 'start' | 'stop',
    name: string = 'ms',
    value: string = '0'
  ) => {
    if (!state.document) return;

    let numericValue = parseInt(value, 10);
    if (isNaN(numericValue)) {
      numericValue = 0;
    }

    const currentItemIndex = state.document.document.findIndex(
      d => d.id === id
    );
    const segment = state.document.document[currentItemIndex];

    let newTime: number;

    // When updating the entire millisecond value
    if (name === 'ms') {
      newTime = numericValue; // Use it as a full replacement
    } else {
      // When updating a specific time unit (hours, minutes, seconds)
      const baseTime = field === 'start' ? segment.start : segment.stop;
      const date = new Date(baseTime);

      switch (name) {
        case 'hours':
          date.setHours(numericValue);
          break;
        case 'minutes':
          date.setMinutes(numericValue);
          break;
        case 'seconds':
          date.setSeconds(numericValue);
          break;
        default:
          return; // Do nothing for unrecognized fields
      }

      newTime = date.getTime();
    }

    // Build the updated document
    const updatedDocument: DocumentSubtitleV1 = {
      ...state.document,
      document: state.document.document.map(
        (docItem: DocumentSubtitleSegmentV1) => {
          if (docItem.id === id) {
            return {
              ...docItem,
              [field]: newTime
            };
          } else {
            return docItem;
          }
        }
      )
    };

    setDocument(updatedDocument);
  };

  const updateSegmentTime = (segment: Segment): void => {
    if (!documentRef.current || !segment.id) return;

    const segmentId = parseInt(segment.id.toString());
    const existingSegment = documentRef.current.document.find(
      segment => segment.id === segmentId
    );
    if (!existingSegment) return;

    const updatedDocument: DocumentSubtitleV1 = {
      ...documentRef.current,
      document: documentRef.current.document.map(
        (docItem: DocumentSubtitleSegmentV1) => {
          if (docItem.id === segmentId) {
            return {
              ...docItem,
              start: parseInt((segment.startTime * 1000).toString()),
              stop: parseInt((segment.endTime * 1000).toString()),
              // Always use the current text from the document, not from Peaks segment
              text: docItem.text
            };
          } else {
            return docItem;
          }
        }
      )
    };

    setDocument(updatedDocument);
  };

  const changeSegmentTimeForCurrentIndex = (
    field: 'start' | 'stop',
    milliseconds: number
  ): void => {
    if (
      state.clickedIndex === null ||
      state.clickedIndex === undefined ||
      !state.document
    )
      return;

    const clickedId = state.clickedIndex;
    const currentSegment = state.document.document.find(
      s => s.id === clickedId
    );
    if (!currentSegment) return;

    const time = nudgeSegmentTime(currentSegment, field, milliseconds);

    const updatedDocument: DocumentSubtitleV1 = {
      ...state.document,
      document: state.document.document.map(
        (docItem: DocumentSubtitleSegmentV1) => {
          if (docItem.id === clickedId) {
            return { ...docItem, [field]: time };
          }
          return docItem;
        }
      )
    };

    setDocument(updatedDocument);
  };

  const setTimeAndSegment = (start: number) => {
    setTime(start);

    const time = start * 1000;
    const segment = state.document?.document.find(
      seg => time >= seg.start && time <= seg.stop
    );
    if (!segment) {
      setClickedIndex(null);
      return;
    }
    setClickedIndex(segment.id);
  };

  // Tracks the wave_info_url the peaks instance was last initialized against,
  // so regenerate-audio-timeline swaps the waveform instead of leaving the
  // previous one in place.
  const lastInitWaveInfoUrlRef = useRef<string | null>(null);

  const handleMediaElementReady = (waveInfoUrl?: string) => {
    if (!mediaRef?.current) return;
    const mediaElement = mediaRef.current.getInternalPlayer();
    if (!mediaElement) return;
    mediaElement.id = 'media-element';
    if (!waveInfoUrl) return;
    if (lastInitWaveInfoUrlRef.current === waveInfoUrl) return;
    lastInitWaveInfoUrlRef.current = waveInfoUrl;
    initializePeaks(waveInfoUrl);
  };

  const addVisibleSegments = (
    peaks: PeaksInstance,
    document: DocumentSubtitleV1,
    centerTime: number
  ) => {
    const windowStart = Math.max(centerTime - 120000, 0);
    const windowEnd = centerTime + 120000;

    // Standard interval overlap: segment overlaps window if it starts before
    // window ends AND stops after window starts
    const visibleSegments =
      document?.document?.filter(segment => {
        return (
          segment.start < windowEnd &&
          segment.stop > windowStart &&
          segment.stop > segment.start
        );
      }) || [];

    // Diff-based update: only add/remove/update what changed to avoid
    // flicker, preserve drag state, and prevent unnecessary re-renders
    const desiredIds = new Set(visibleSegments.map(s => s.id.toString()));
    const currentSegments = peaks.segments.getSegments();
    const currentIds = new Set(currentSegments.map(s => s.id));

    // Remove segments no longer in range
    for (const seg of currentSegments) {
      if (seg.id && !desiredIds.has(seg.id)) {
        peaks.segments.removeById(seg.id);
      }
    }

    // Add new segments or update existing ones with changed text
    for (const segment of visibleSegments) {
      const cleanText = segment.text.replace(/&nbsp;/g, ' ').trim();
      const id = segment.id.toString();

      if (currentIds.has(id)) {
        const existing = peaks.segments.getSegment(id);
        // Don't yank a segment the user is actively dragging
        const isDraggingThis =
          isDraggingRef.current && draggingSegmentIdRef.current === id;
        if (existing && !isDraggingThis) {
          const newStart = Math.max(0, segment.start / 1000);
          const newEnd = Math.max(newStart, segment.stop / 1000);
          const timesChanged =
            Math.abs(existing.startTime - newStart) > 0.001 ||
            Math.abs(existing.endTime - newEnd) > 0.001;
          if (timesChanged || existing.labelText !== cleanText) {
            existing.update({
              startTime: newStart,
              endTime: newEnd,
              labelText: cleanText
            });
          }
        }
      } else if (!peaks.segments.getSegment(id)) {
        // Ensure startTime and endTime are valid (non-negative)
        const startTime = Math.max(0, segment.start / 1000);
        const endTime = Math.max(startTime, segment.stop / 1000);
        peaks.segments.add({
          startTime,
          endTime,
          labelText: cleanText,
          // eslint-disable-next-line no-restricted-syntax
          color: '#fff',
          id,
          editable: true
        });
      }
    }
  };

  const initializePeaks = (url: string) => {
    const containerElement = document.getElementById(
      'peaks-overview-container'
    );
    const mediaElement = document.getElementById('media-element');
    if (!containerElement || !mediaElement) return;

    // Ensure container has valid dimensions before initializing peaks.js
    const { clientWidth, clientHeight } = containerElement;
    if (clientWidth <= 0 || clientHeight <= 0) return;

    Peaks.init(
      {
        overview: {
          container: containerElement
        },
        zoomview: {
          container: containerElement,
          playheadClickTolerance: 5,
          autoScroll: true,
          autoScrollOffset: 35,
          // eslint-disable-next-line no-restricted-syntax
          axisGridlineColor: '#535353',
          // eslint-disable-next-line no-restricted-syntax
          axisLabelColor: '#535353',
          playheadWidth: 2
        },
        mediaElement: mediaElement,
        dataUri: {
          arraybuffer: url
        },
        keyboard: true,
        // eslint-disable-next-line no-restricted-syntax
        waveformColor: '#8D8D8D',
        // eslint-disable-next-line no-restricted-syntax
        playheadColor: '#006eb0',
        waveformCache: true,
        showPlayheadTime: false,
        zoomLevels: [512, 1024, 2048, 4096],
        segmentOptions: {
          markers: false,
          overlay: true,
          // eslint-disable-next-line no-restricted-syntax
          waveformColor: '#006eb0',
          // eslint-disable-next-line no-restricted-syntax
          overlayColor: '#fff',
          overlayOpacity: 1,
          overlayBorderWidth: 0,
          overlayCornerRadius: 6,
          overlayOffset: 35,
          overlayLabelAlign: 'left',
          overlayLabelVerticalAlign: 'top',
          overlayLabelPadding: 5,
          // eslint-disable-next-line no-restricted-syntax
          overlayLabelColor: '#000000',
          overlayFontFamily: 'sans-serif',
          overlayFontSize: 11,
          overlayFontStyle: 'normal'
        },
        createSegmentLabel: function () {
          return null;
        }
      },
      (err, peaks) => {
        if (err) {
          return;
        }
        if (!peaks) return;

        const view = peaks.views.getView('zoomview');
        view?.setZoom({
          scale: 256
        });
        view?.setWheelMode('scroll', {
          captureVerticalScroll: true
        });
        view?.setAmplitudeScale(2.3);
        view?.showAxisLabels(true, {
          topMarkerHeight: 0,
          bottomMarkerHeight: 10
        });
        view?.setMinSegmentDragWidth(50);
        view?.enableSegmentDragging(true);
        view?.setSegmentDragMode('no-overlap');
        view?.setWaveformDragMode('scroll');

        peaks.on('overview.click', function (event) {
          setTime(event.time, 'peaks');
        });

        let lastSegmentUpdate = 0;
        let trailingTimer: ReturnType<typeof setTimeout> | null = null;
        const SEGMENT_UPDATE_THROTTLE = 500; // ms

        // Leading + trailing edge throttle: guarantees the final centerMs
        // is processed even if events arrive rapidly (e.g. fast scroll bursts)
        // — otherwise the visible window can end up without segments.
        const scheduleSegmentUpdate = (centerMs: number) => {
          if (!documentRef.current) return;
          const now = Date.now();
          const elapsed = now - lastSegmentUpdate;
          if (elapsed >= SEGMENT_UPDATE_THROTTLE) {
            lastSegmentUpdate = now;
            addVisibleSegments(peaks, documentRef.current, centerMs);
            if (trailingTimer) {
              clearTimeout(trailingTimer);
              trailingTimer = null;
            }
          } else {
            if (trailingTimer) clearTimeout(trailingTimer);
            trailingTimer = setTimeout(() => {
              trailingTimer = null;
              if (!documentRef.current) return;
              lastSegmentUpdate = Date.now();
              addVisibleSegments(peaks, documentRef.current, centerMs);
            }, SEGMENT_UPDATE_THROTTLE - elapsed);
          }
        };

        peaks.on('player.timeupdate', time => {
          scheduleSegmentUpdate(time * 1000);
        });

        peaks.on('player.seeked', time => {
          scheduleSegmentUpdate(time * 1000);
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (peaks as any).on(
          'zoomview.scroll',
          (startTime: number, endTime: number) => {
            scheduleSegmentUpdate(((startTime + endTime) / 2) * 1000);
          }
        );

        peaks.on('segments.dragstart', function (event) {
          isDraggingRef.current = true;
          draggingSegmentIdRef.current = event.segment?.id?.toString() ?? null;
        });

        peaks.on('segments.dragend', function (event) {
          isDraggingRef.current = false;
          draggingSegmentIdRef.current = null;
          if (!event.segment || !event.segment.id) {
            return;
          }
          updateSegmentTime(event.segment);
        });

        initializePeaksInAudio(peaks);

        if (documentRef.current) {
          const initialTime = documentRef.current.document?.[0]?.start || 0;
          addVisibleSegments(peaks, documentRef.current, initialTime);
        }
      }
    );
  };

  const finishDocument = (
    sessionId: string,
    documentId: string,
    newStatus: boolean,
    setFinished: (status: boolean) => void
  ) => {
    if (!state.document || !state.documentResult || !state.session) return;

    if (!newStatus) {
      toggleStatus(
        { sessionId, documentId, status: newStatus },
        {
          onSuccess: () => {
            toast.success(
              t('status-updated-successfully', { status: 'false' })
            );
            setFinished(newStatus);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            const errorMessage =
              error.response?.data?.error_description ||
              error.response?.data.detail ||
              error.response?.data.error ||
              error.response?.data.message ||
              t('failed-to-update-status');
            toast.error(errorMessage);
          }
        }
      );
      return;
    }

    updateDocument(
      {
        sessionId: state.session.session_id,
        documentId: state.documentResult!.result.id!,
        document: state.document as DocumentObjectInnerDocument
      },
      {
        onSuccess: () => {
          toggleStatus(
            { sessionId, documentId, status: newStatus },
            {
              onSuccess: () => {
                toast.success(
                  t('status-updated-successfully', { status: 'true' })
                );
                setFinished(newStatus);
                setIsDirty(false);
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onError: (error: any) => {
                const errorMessage =
                  error.response?.data?.error_description ||
                  error.response?.data.detail ||
                  error.response?.data.error ||
                  error.response?.data.message ||
                  t('failed-to-update-status');
                toast.error(errorMessage);
                setFinished(!newStatus);
              }
            }
          );
        },
        onError: () => {
          toast.error(t('failed-to-update-status'));
          setFinished(!newStatus);
        }
      }
    );
  };

  /**
   * Record the server version produced by this tab's own save so the next
   * autosave does not mistake our own write for somebody else's. Prefers the
   * `last_modified` echoed by the PUT response, falling back to one lightweight
   * probe. If both fail the baseline stays put and the next autosave holds the
   * draft locally rather than overwriting on no evidence (issue #2347).
   */
  const recordOwnSave = async (
    sessionId: string,
    documentId: string,
    response: unknown
  ) => {
    const key = syncKey(sessionId, documentId);
    const fromResponse = lastModifiedFromUpdateResponse(response);
    if (fromResponse > 0) {
      setBaseline(key, fromResponse);
      return;
    }
    const probed = await probeSharedServerLastModified(
      sessionId,
      documentId,
      token
    );
    if (probed != null) setBaseline(key, probed);
  };

  const saveDocument = (userAction?: boolean) => {
    if (!state.document || !state.documentResult || !state.session) return;

    // Data-loss guard (Den Haag incident, 2026-06): never PUT an empty document
    // to the server. An empty editor state is the symptom of a corrupted/stale
    // draft, not a real edit — saving it would wipe the server copy's segments.
    const docToSave = documentRef.current ?? state.document;
    if (!docToSave?.document || docToSave.document.length === 0) {
      setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
      return;
    }

    // Set status to saving
    setSaveStatus('saving');

    updateDocument(
      {
        sessionId: state.session.session_id,
        documentId: state.documentResult!.result.id!,
        document: docToSave as DocumentObjectInnerDocument,
        plain_document_changed: !!userAction
      },
      {
        onSuccess: response => {
          toast.success('Document updated successfully!');
          const now = Date.now();
          void recordOwnSave(
            state.session!.session_id,
            state.documentResult!.result.id!,
            response
          );
          setIsDirty(false);
          setSaveStatus('synced');
          setLastServerSync(now);

          // Clear recovery flag - data is now synced with server
          hasRecoveredData.current = false;

          // Update localStorage with synced status and current undo/redo stacks.
          // Use refs (not state) to capture the live document at save completion time,
          // avoiding stale-closure bugs where the async onSuccess sees an old state snapshot.
          if (state.session && state.documentResult && documentRef.current) {
            saveSharedCaptionToLocalStorage(
              state.session.session_id,
              state.documentResult.result.id,
              {
                document: documentRef.current,
                undoStack: undoStackRef.current,
                redoStack: redoStackRef.current,
                isDirty: false,
                lastLocalSave: state.lastLocalSave ?? undefined,
                lastServerSync: now,
                filename: state.documentResult.result.filename,
                timecodeOffset: state.documentResult.result.timecode_offset
              }
            );
          }
        },
        onError: () => {
          toast.error('Error updating document');

          // Revert to saved-locally status on server error
          setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
        }
      }
    );
  };

  const editorUpdateSegment = (children: Descendant[]) => {
    if (
      !state.document ||
      !state.session ||
      !children ||
      children.length === 0
    ) {
      return;
    }

    const childrenDocument = children.find(
      child => typeof (child as CaptionScriptixDescendant).id === 'number'
    ) as CaptionScriptixDescendant & {
      children: (Descendant & {
        text: string;
      })[];
    };

    if (!childrenDocument || typeof childrenDocument.id !== 'number') return;

    const segment = state.document.document.find(
      s => s.id === childrenDocument.id
    );

    if (!segment) return;

    const index = state.document.document.indexOf(segment);
    if (index === -1) return;

    const newText = childrenDocument.children?.[0]?.text ?? '';

    if (segment.text === newText) return;

    const updatedDocument = [...state.document.document];
    updatedDocument[index] = {
      ...segment,
      text: newText
    };

    const newPayload: DocumentSubtitleV1 = {
      ...state.document,
      document: updatedDocument
    };

    const now = Date.now();
    if (startsTextUndoStep(textUndoStepRef.current, segment.id, now)) {
      addVersionChange(newPayload);
    }
    textUndoStepRef.current = { segmentId: segment.id, at: now };

    // Update both the state and the document reference
    dispatch({
      type: 'SET_DOCUMENT',
      payload: newPayload
    });
    documentRef.current = newPayload;

    if (peaksInstance) {
      // Immediately refresh Peaks segments with updated text
      addVisibleSegments(peaksInstance, newPayload, currentTime * 1000);
    }

    setIsDirty(true);

    // Mark as unsaved and trigger debounced localStorage save
    setSaveStatus('unsaved');
    debouncedLocalStorageSave();
  };

  /**
   * Autosave, fired by the shared editor's 120-second interval.
   *
   * Stale-tab guard (issue #2347): a shared tab left open on an old copy used to
   * PUT that entire copy every interval, replacing newer edits saved from
   * another tab on the same link. The gate refuses to write when there is
   * nothing to save, when the server's state cannot be read, or when the server
   * has moved past the version this tab is based on.
   *
   * The shared editor has no concurrent-edit banner, so a conflict surfaces as a
   * one-off toast and the draft stays in localStorage until the user saves
   * explicitly or reopens the document.
   */
  const silentSave = async (updatedDocument?: DocumentSubtitleV1) => {
    if (!state.documentResult || !state.session) return;

    const sessionId = state.session.session_id;
    const documentId = state.documentResult.result.id!;
    const key = syncKey(sessionId, documentId);
    const docToSave = updatedDocument ?? state.document;

    // Data-loss guard (Den Haag incident, 2026-06): never autosave an empty
    // document over the server copy — `resolveAutosave` reports `skip-empty`.
    const gateInput = {
      segmentCount: docToSave?.document?.length ?? 0,
      hasLocalChanges: hasPendingLocalChanges(state.isDirty, state.saveStatus),
      baselineLastModified: getBaseline(key)
    };

    let decision = resolveAutosave(gateInput);
    if (decision === 'needs-probe') {
      const serverLastModified = await probeSharedServerLastModified(
        sessionId,
        documentId,
        token
      );
      decision = resolveAutosave({
        ...gateInput,
        baselineLastModified: getBaseline(key),
        serverLastModified
      });
    }

    if (decision === 'conflict') {
      if (!hasWarnedRemoteEdit.current) {
        hasWarnedRemoteEdit.current = true;
        toast(
          `${t('document-updated-remotely')} ${t(
            'concurrent-edit-autosave-paused'
          )}`
        );
      }
      return;
    }

    if (decision !== 'save') return;

    // Re-read the live document now that the preflight has resolved. The probe
    // is a network round trip, and anything the user typed during it landed in
    // `documentRef` after `docToSave` was captured — sending that stale snapshot
    // and then clearing the dirty flag would strand those keystrokes, because
    // the next tick would read the tab as clean and skip them (#2354 review).
    const docAtSend = updatedDocument ?? documentRef.current ?? state.document;
    if (!docAtSend?.document || docAtSend.document.length === 0) return;

    // Set status to saving (but don't show toast)
    setSaveStatus('saving');

    updateDocument(
      {
        sessionId,
        documentId,
        document: docAtSend as DocumentObjectInnerDocument,
        dontInvalidateQueries: true
      },
      {
        onSuccess: response => {
          // The server moved either way, so the baseline always advances.
          void recordOwnSave(sessionId, documentId, response);
          const now = Date.now();
          setLastServerSync(now);

          // Only the revision we actually sent is clean. Every mutation path
          // replaces the document object and reassigns `documentRef`, so an
          // identity check tells us whether the user edited while the PUT was
          // in flight. If they did, this tab still holds unsaved work: leave
          // the dirty flag alone so the next autosave pushes the newer text.
          if (documentRef.current !== docAtSend) {
            setSaveStatus('unsaved');
            return;
          }

          setIsDirty(false);

          // Mark as synced with server
          setSaveStatus('synced');

          // Clear recovery flag - data is now synced with server
          hasRecoveredData.current = false;

          // Update localStorage with sync timestamp
          if (state.session && state.documentResult) {
            markAsSynced(
              state.session.session_id,
              state.documentResult.result.id
            );
          }
        },
        onError: () => {
          // Silently revert to saved-locally status on server error
          setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
        }
      }
    );
  };

  const setSearch = (query: string) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  };

  const setCurrentMatch = (match: DocumentSubtitleSegmentV1[] | null) => {
    dispatch({ type: 'SET_CURRENT_MATCH', payload: match });
  };

  const setCaseSensitive = (caseSensitive: boolean) => {
    dispatch({ type: 'SET_CASE_SENSITIVE', payload: caseSensitive });
  };

  const setWholeWord = (wholeWord: boolean) => {
    dispatch({ type: 'SET_WHOLE_WORD', payload: wholeWord });
  };

  /**
   * Replace the whole document from a bulk operation (Find and Replace, or a
   * font applied to every cue).
   *
   * Dirty tracking (issue #2347): this used to update state and the ref only,
   * so a bulk edit was never flagged dirty, never written to localStorage, and
   * indistinguishable from an untouched tab. It now moves both flags and
   * schedules the localStorage write, exactly like `setDocument`.
   */
  const updateCaptionDocument = (document: DocumentSubtitleV1) => {
    dispatch({ type: 'SET_DOCUMENT', payload: document });
    documentRef.current = document;
    setIsDirty(true);
    setSaveStatus('unsaved');
    debouncedLocalStorageSave();
  };

  const setIsDirty = (isDirty: boolean) => {
    dispatch({ type: 'SET_IS_DIRTY', payload: isDirty });
  };

  useEffect(() => {
    if (!state.document && captionDocument) {
      dispatch({
        type: 'SET_DOCUMENT',
        payload: captionDocument.result.document as DocumentSubtitleV1
      });
    }
    if (!state.documentResult && captionDocument) {
      dispatch({ type: 'SET_DOCUMENT_RESULT', payload: captionDocument });
    }
  }, [captionDocument]);

  // Seed the autosave baseline with the server version this editor opened on.
  // The shared editor has no concurrent-edit poller to do it, and without a
  // baseline every autosave would resolve to `skip-unverified` and never run.
  //
  // Once only, and never on a later refetch (#2354 review): the sync effect
  // above adopts a server body only on first load, so a refetch that advanced
  // the baseline would claim this tab is based on a server revision whose body
  // it never adopted — and the next autosave would then overwrite that
  // revision's edits. The baseline advances only where the body does: on our
  // own save, or on an explicit reload.
  const hasSeededBaseline = useRef(false);
  useEffect(() => {
    if (hasSeededBaseline.current) return;
    if (!session || !captionDocument) return;
    hasSeededBaseline.current = true;
    setBaseline(
      syncKey(session.session_id, captionDocument.result.id),
      captionDocument.result.last_modified
    );
  }, [session, captionDocument]);

  // --- Helper: resolve the active segment array index from clickedIndex (which is always a segment ID)
  const getActiveIndex = useCallback((): number | null => {
    const list = state.document?.document ?? [];
    if (!list.length) return null;

    const n = state.clickedIndex;
    if (n === null || n === undefined) return null;

    // clickedIndex is always a segment ID — find its array index
    const idx = list.findIndex(s => s.id === n);
    return idx === -1 ? null : idx;
  }, [state.document?.document, state.clickedIndex]);

  // --- Timing offset core (shifts start & stop, clamps at 0, pushes to undo, marks dirty, refreshes Peaks)
  const applyTimingOffset = useCallback(
    (deltaMs: number, mode: 'auto' | 'all' | 'fromActive' = 'auto') => {
      const doc = state.document;
      if (!doc?.document?.length || !deltaMs) return;

      const items = doc.document;
      const activeIdx = getActiveIndex();
      const fromIndex =
        mode === 'all'
          ? 0
          : mode === 'fromActive'
            ? (activeIdx ?? 0)
            : /* auto */ (activeIdx ?? 0);

      const shifted = items.map((seg, i) => {
        if (i < fromIndex) return seg;

        // clamp-aware shift (so negative shifts at the start won't go below 0)
        const newStart = Math.max(0, seg.start + deltaMs);
        const actualShift = newStart - seg.start; // may be smaller than deltaMs if clamped
        const newStop = Math.max(newStart, seg.stop + actualShift);

        return { ...seg, start: newStart, stop: newStop };
      });

      // Build new payload
      const newPayload = { ...doc, document: shifted };

      // Version / undo
      addVersionChange(newPayload);
      dispatch({ type: 'SET_DOCUMENT', payload: newPayload });
      documentRef.current = newPayload;
      setIsDirty(true);

      // Mark as unsaved and trigger debounced localStorage save
      setSaveStatus('unsaved');
      debouncedLocalStorageSave();

      // Refresh Peaks view
      if (peaksInstance) {
        addVisibleSegments(peaksInstance, newPayload, currentTime * 1000);
      }

      // Light validation + UX feedback
      let overlapDetected = false;
      for (let i = 1; i < shifted.length; i++) {
        if (shifted[i].start < shifted[i - 1].stop) {
          overlapDetected = true;
          break;
        }
      }

      if (overlapDetected) {
        toast(t('shift-applied-with-warnings', 'Shift applied with warnings'), {
          icon: '⚠️'
        });
      } else {
        const seconds = (deltaMs / 1000).toFixed(3);
        const scope =
          fromIndex === 0
            ? t('all-segments', 'all segments')
            : t('from-active-onward', 'from active segment onward');
        toast.success(
          t('shifted-by-x-on-scope', 'Shifted by {{seconds}}s ({{scope}})', {
            seconds,
            scope
          })
        );
      }
    },
    [
      state.document,
      getActiveIndex,
      addVersionChange,
      dispatch,
      peaksInstance,
      currentTime,
      t,
      setSaveStatus,
      debouncedLocalStorageSave
    ]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (localStorageSaveTimer.current) {
        clearTimeout(localStorageSaveTimer.current);
      }
    };
  }, []);

  // --- Conflict resolution (DraftConflictModal) -----------------------------
  // Apply the local draft over the server baseline and continue editing it.
  const resolveConflictKeepLocal = useCallback(() => {
    if (!conflict) return;
    dispatch({ type: 'SET_DOCUMENT', payload: conflict.localDocument });
    dispatch({ type: 'SET_UNDO_STACK', payload: conflict.localUndo });
    dispatch({ type: 'SET_REDO_STACK', payload: conflict.localRedo });
    documentRef.current = conflict.localDocument;
    undoStackRef.current = conflict.localUndo;
    redoStackRef.current = conflict.localRedo;
    // Prevent the API document from overwriting the recovered local draft.
    hasRecoveredData.current = true;
    // The user has seen the cloud version in the conflict modal and chose this
    // draft over it, so the tab is now deliberately based on that server
    // version: autosave is free to overwrite it (issue #2347). Without this the
    // gate would keep reporting a conflict and only an explicit save would get
    // through.
    if (state.session && state.documentResult) {
      setBaseline(
        syncKey(state.session.session_id, state.documentResult.result.id),
        conflict.serverTimestamp
      );
    }
    setIsDirty(true);
    setSaveStatus('saved-locally');
    setLastLocalSave(conflict.localTimestamp || Date.now());
    setConflict(null);
  }, [
    conflict,
    state.session,
    state.documentResult,
    setSaveStatus,
    setLastLocalSave
  ]);

  // Discard the local draft and keep the server copy (already the live state).
  const resolveConflictLoadCloud = useCallback(() => {
    if (state.session && state.documentResult) {
      clearSharedCaptionFromLocalStorage(
        state.session.session_id,
        state.documentResult.result.id
      );
    }
    setSaveStatus('synced');
    setConflict(null);
  }, [state.session, state.documentResult, setSaveStatus]);

  return {
    contextValue: {
      ...state,
      setQuery,
      setDocument,
      historyVersion,
      undo,
      redo,
      reset,
      addListItem,
      removeListItem,
      splitListItem,
      joinListItems,
      cleanDocument,
      saveDocument,
      searchInputRef,
      setClickedIndex,
      cleanup: clearHistoryStack,
      applyStyle,
      applyFontToAll,
      saveDocumentLoading,
      documentResetting,
      cleanDocumentLoading,
      finishedLoading,
      changeSegmentTime,
      changeSegmentTimeForCurrentIndex,
      setTimeAndSegment,
      handleMediaElementReady,
      finishDocument,
      setEditor,
      editorUpdateSegment,
      autoSave: silentSave,
      setSearch,
      setCurrentMatch,
      setCaseSensitive,
      setWholeWord,
      updateCaptionDocument,
      applyTimingOffset,
      getActiveIndex,
      saveStatus: state.saveStatus,
      lastLocalSave: state.lastLocalSave,
      lastServerSync: state.lastServerSync
    },
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  };
}
